import type { BaseClaim, DraftItem } from './api';

// Unsent website edits survive a failed save, a closed editor or a reload:
// they are kept on this device until the API confirmed them. Entries are
// scoped to the signed-in user, restaurant, website and target, and removed
// on sign-out, so nobody else on a shared device sees them.

// Everything of this app on the device starts with ROOT (cleared on sign-out).
const ROOT = 'managedWebsites:';
const PREFIX = `${ROOT}draft:v1:`;
const ANSWER_PREFIX = `${ROOT}answer:v1:`;

export type Scope = { uid: string; restaurantId: string; websiteId: string; target: string };

export type LocalDraft = {
  /** The server revision these edits were made on. */
  revision: number;
  items: DraftItem[];
  /**
   * What the editor showed when these edits were made (their trusted base),
   * null when the server already holds their base. Missing in entries written
   * before it was recorded: their base is unknown and never guessed.
   */
  shown?: BaseClaim | null;
  /** A submission whose outcome is unknown (lost answer). */
  submission: { id: string; revision: number; items: DraftItem[]; intent: 'publish' | 'request' } | null;
  savedAt: number;
};

const keyOf = (scope: Scope) =>
  `${PREFIX}${[scope.uid, scope.restaurantId, scope.websiteId, scope.target].map(encodeURIComponent).join(':')}`;

const storage = () => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export const readLocalDraft = (scope: Scope): LocalDraft | null => {
  try {
    const raw = storage()?.getItem(keyOf(scope));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LocalDraft;
    return Array.isArray(parsed?.items) && Number.isInteger(parsed?.revision) ? parsed : null;
  } catch {
    return null;
  }
};

/** True only once the edits are really on the device. */
export const writeLocalDraft = (scope: Scope, draft: LocalDraft) => {
  // No storage at all (the browser denies even access to it) keeps nothing either.
  const store = storage();
  if (!store) return false;
  try {
    store.setItem(keyOf(scope), JSON.stringify(draft));
    return true;
  } catch {
    // Full or blocked storage: the editor then says that edits are not saved.
    return false;
  }
};

export const removeLocalDraft = (scope: Scope) => {
  try {
    storage()?.removeItem(keyOf(scope));
  } catch {
    // Nothing to clean up.
  }
};

/** On sign-out: no unsent edits or answers of any user stay on the device. */
export const clearLocalDrafts = () => {
  const store = storage();
  if (!store) return;
  for (let index = store.length - 1; index >= 0; index--) {
    const key = store.key(index);
    if (key?.startsWith(ROOT)) store.removeItem(key);
  }
};

/** An answer submission whose outcome is not known yet. */
export type PendingAnswer = { changeRequestId: string; questionId: string; revision: number; submissionId: string; at: number };

export type PendingAnswerStore = {
  /** Oldest first; only those of `changeRequestId` if given. */
  list: (changeRequestId?: string) => PendingAnswer[];
  put: (answer: PendingAnswer) => void;
  remove: (answer: PendingAnswer) => void;
};

/**
 * Pending answers of one user on one website, one entry per request and
 * question. Kept in memory as well, so a blocked storage still protects the
 * open page.
 */
export const pendingAnswerStore = (scope: Omit<Scope, 'target'>): PendingAnswerStore => {
  const base = `${ANSWER_PREFIX}${[scope.uid, scope.restaurantId, scope.websiteId].map(encodeURIComponent).join(':')}:`;
  const keyOf = (answer: Pick<PendingAnswer, 'changeRequestId' | 'questionId'>) =>
    `${base}${encodeURIComponent(answer.changeRequestId)}:${encodeURIComponent(answer.questionId)}`;
  const memory = new Map<string, PendingAnswer>();

  const stored = () => {
    const found = new Map(memory);
    const store = storage();
    if (!store) return found;
    try {
      for (let index = 0; index < store.length; index++) {
        const key = store.key(index);
        if (!key?.startsWith(base)) continue;
        const answer = JSON.parse(store.getItem(key) ?? 'null') as PendingAnswer | null;
        if (answer && typeof answer.submissionId === 'string' && Number.isInteger(answer.revision)) found.set(key, answer);
      }
    } catch {
      // Unreadable entries are skipped; the API stays the authority.
    }
    return found;
  };

  return {
    list: (changeRequestId) =>
      [...stored().values()]
        .filter((answer) => !changeRequestId || answer.changeRequestId === changeRequestId)
        .sort((a, b) => a.at - b.at),
    put: (answer) => {
      memory.set(keyOf(answer), answer);
      try {
        storage()?.setItem(keyOf(answer), JSON.stringify(answer));
      } catch {
        // Kept in memory for this page.
      }
    },
    remove: (answer) => {
      // Only exactly this submission: a newer one for the same question stays.
      const key = keyOf(answer);
      if (memory.get(key)?.submissionId === answer.submissionId) memory.delete(key);
      try {
        const current = JSON.parse(storage()?.getItem(key) ?? 'null') as PendingAnswer | null;
        if (current?.submissionId === answer.submissionId) storage()?.removeItem(key);
      } catch {
        // Nothing to clean up.
      }
    },
  };
};
